import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "role-chip role-chip--student role-chip--active",
  "aria-current": "true",
  "data-testid": "role-chip-student"
}
const _hoisted_2 = ["aria-label", "aria-current", "aria-busy", "aria-expanded"]
const _hoisted_3 = {
  key: 0,
  class: "role-chip__dot role-chip__dot--pulse",
  "aria-hidden": "true",
  "data-testid": "role-chip-pulse"
}
const _hoisted_4 = {
  key: 1,
  class: "role-chip__dot role-chip__dot--payout",
  "aria-hidden": "true",
  "data-testid": "role-chip-payout-dot"
}
const _hoisted_5 = { class: "role-chip-tooltip__text" }

import { computed, ref } from 'vue';

import type { Phase } from '@/composables/useMentorApplicationStatus';
import MentorApplicationStatusModal from '@/components/common/MentorApplicationStatusModal.vue';

interface Props {
    /** Текущая роль из user-записи (Vuex `userRole`). */
    currentRole: 'mentor' | 'student';
    /** Нормализованная фаза заявки из useMentorApplicationStatus(). */
    applicationPhase: Phase;
    /** Причина отказа от модератора (используется только при phase=rejected). */
    rejectionReason?: string;
    /**
     * 2026-06-01 hotfix: одобренный наставник, но способ выплат ещё не подключён
     * (``selling_readiness === 'approved_no_payout'``). Заменяет собой
     * прежний sticky-бар, который занимал целую строку под header'ом. В этом
     * состоянии на ДЕСКТОПЕ чип остаётся «подтверждённым» (фиолетовая заливка) и
     * получает янтарный action-суффикс «· Подключить карту»; на МОБИЛЬНОМ
     * (compact) — оранжевый чип + мигающая точка (P3, см. ``isPayoutAlert``).
     * Клик ведёт в настройки приёма денег. Контейнер вычисляет флаг из
     * ``useSellingReadiness().value``.
     */
    payoutMissing?: boolean;
    /**
     * Компактный (мобильный) режим. Контейнер выставляет на узком вьюпорте
     * (HeaderDefault → ``smAndDown``), где суффикс чипа скрыт по ширине.
     * В этом режиме:
     *   • payout-missing показываем ОРАНЖЕВЫМ чипом + МИГАЮЩЕЙ точкой (P3),
     *     т.к. текстовый суффикс скрыт по ширине;
     *   • hover/focus-раскрытие tooltip отключаем (на touch их нет) и вместо
     *     навигации первый тап по payout-чипу РАСКРЫВАЕТ tooltip с объяснением,
     *     а повторный — ведёт в настройки выплат.
     */
    compact?: boolean;
}

const groupLabel = 'Ваш статус на платформе';

// ── Единое состояние отображения ──────────────────────────────────────

type DisplayState = 'student' | 'mentor_unconfirmed' | 'mentor_confirmed';

/**
 * Маппинг (role, phase) → одно из трёх взаимоисключающих состояний.
 *
 *   revoked (перебивает role=mentor)               → mentor_unconfirmed
 *   approved | role=mentor                         → mentor_confirmed
 *   draft | pending | changes_requested | rejected → mentor_unconfirmed
 *   none | cancelled                               → student
 *
 * `rejected` оставлен в «не подтверждён» (а не в «ученик»), чтобы статус
 * заявки и её причина оставались видимыми в tooltip — пользователь ещё в
 * процессе становления наставником и может подать заново.
 *
 * `revoked` проверяется ПЕРВЫМ: у отозванного админом наставника роль в
 * user-записи остаётся `mentor` (снимается только selling_readiness), и без
 * этого приоритета он видел бы фиолетовый «профиль подтверждён».
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'UserRoleChips',
  props: {
    currentRole: {},
    applicationPhase: {},
    rejectionReason: { default: '' },
    payoutMissing: { type: Boolean, default: false },
    compact: { type: Boolean, default: false }
  },
  emits: ["click-mentor-chip", "click-payout-attach"],
  setup(__props: any, { emit: __emit }) {

/**
 * UserRoleChips — единый чип статуса пользователя на платформе.
 *
 * Pure presentational (audit §2.2 "Container/Presentational"): не читает
 * Vuex/composables напрямую, не ходит в API. Контейнер (HeaderDefault,
 * DashboardPage, ProfileMenu) подтягивает `useMentorApplicationStatus` и
 * прокидывает уже нормализованные props.
 *
 * Модель состояний (редизайн по требованию 29.05.2026) — ТРИ статуса:
 *   1. «Ученик»                — нет активной заявки наставника.
 *   2. «Наставник» (серый)     — заявка в процессе / на модерации / отклонена,
 *                                т.е. наставник «не подтверждён».
 *   3. «Наставник» (цветной)   — заявка одобрена, полноценный наставник.
 *
 * Никаких фазовых подписей в самой надписи чипа — различие «подтверждён /
 * не подтверждён» передаётся ЦВЕТОМ, детали — в tooltip.
 *
 * A11y (audit §3 — WCAG 2.1 AA):
 *   - role="group" + aria-label на контейнере;
 *   - «Ученик» — неинтерактивный <span> со status-семантикой (aria-current);
 *   - «Наставник» — настоящий <button type="button"> с aria-label,
 *     aria-describedby (id тела tooltip), aria-current (подтверждён),
 *     aria-busy (на модерации);
 *   - pulse-dot декоративный (aria-hidden), анимация отключается при
 *     prefers-reduced-motion;
 *   - tooltip закрывается по Escape (Vuetify v3 для button-activator),
 *     дополнительно focus() на чип как защитная мера.
 */
const props = __props;

const emit = __emit;

const mentorChipRef = ref<HTMLButtonElement | null>(null);

// Открыта ли модалка статуса заявки (открывается по клику на чип «на проверке»).
const statusModalOpen = ref(false);

// Управляемое состояние tooltip. На десктопе им «рулит» Vuetify (hover/focus);
// в compact-режиме — мы сами, чтобы раскрыть подсказку по тапу (touch без hover).
const tooltipOpen = ref(false);

// Стабильный per-instance id для aria-describedby. Math.random дёшев против
// импорта nanoid ради header-виджета; коллизии между инстансами на странице
// не влияют на корректность (id только связывает SR-described-by).
const tooltipId = `role-chip-mentor-tooltip-${Math.random().toString(36).slice(2, 10)}`;

const displayState = computed<DisplayState>(() => {
    if (props.applicationPhase === 'revoked') {
        return 'mentor_unconfirmed';
    }
    if (props.currentRole === 'mentor' || props.applicationPhase === 'approved') {
        return 'mentor_confirmed';
    }
    if (
        props.applicationPhase === 'draft' ||
        props.applicationPhase === 'pending' ||
        props.applicationPhase === 'changes_requested' ||
        props.applicationPhase === 'rejected'
    ) {
        return 'mentor_unconfirmed';
    }
    return 'student';
});

const isConfirmed = computed<boolean>(() => displayState.value === 'mentor_confirmed');

const isPending = computed<boolean>(
    () => props.applicationPhase === 'pending' || props.applicationPhase === 'changes_requested',
);

// ── Презентация чипа «Наставник» ──────────────────────────────────────

const mentorChipClass = computed<string>(() =>
    isConfirmed.value ? 'role-chip--confirmed' : 'role-chip--unconfirmed',
);

/**
 * P3 (report4-14-07-26): компактный (мобильный) чип подтверждённого наставника
 * без привязанной карты. По требованию заказчика в этом состоянии чип —
 * тёплый ОРАНЖЕВЫЙ (перебивает фиолетовый confirmed) с МИГАЮЩЕЙ точкой:
 * на узком экране текстовый суффикс «· Подключить карту» скрыт (HeaderDefault),
 * и нужен заметный сигнал «нужно действие». На десктопе (не compact) НЕ
 * применяется — там остаётся фиолетовый confirmed + видимый суффикс (#250).
 */
const isPayoutAlert = computed<boolean>(
    () => isConfirmed.value && props.payoutMissing && props.compact,
);

/**
 * Краткий статус-суффикс к надписи «Наставник» для НЕподтверждённого
 * наставника. Цель — чтобы пользователю было сразу ПОНЯТНО, на каком он
 * этапе, прямо в чипе (без наведения):
 *   • «заявка не подана» — он начал, но не дозаполнил/не отправил (draft);
 *   • «на проверке»      — отправил, ждёт модерацию (submitted/pending);
 *   • «нужны правки»     — модератор вернул на доработку;
 *   • «отклонена»        — отказ.
 * Для подтверждённого наставника суффикса нет — просто «Наставник».
 */
const mentorSuffix = computed<string>(() => {
    // Confirmed + payout missing — единственный случай, когда у подтверждённого
    // наставника есть суффикс. Прежде эта информация жила в отдельной sticky-полосе
    // под header'ом; теперь конденсирована в пилюлю.
    if (isConfirmed.value) {
        // Побуждающая формулировка (баг 9.1) — консистентно с
        // MentorNoPayoutBanner.vue и PayoutPage.vue: зовём к действию,
        // а не констатируем «карта не привязана».
        return props.payoutMissing ? 'Привязать карту' : '';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'заявка не подана';
        case 'pending':
            return 'на проверке';
        case 'changes_requested':
            return 'нужны правки';
        case 'rejected':
            return 'отклонена';
        case 'revoked':
            return 'доступ отозван';
        default:
            return 'заявка не подана';
    }
});

/**
 * Состояния, где нужно ДЕЙСТВИЕ пользователя (дозаполнить / поправить /
 * подать заново / привязать карту) — суффикс подсвечивается янтарным,
 * чтобы привлечь внимание. «на проверке» — это ожидание, не действие →
 * нейтральный.
 */
const suffixNeedsAction = computed<boolean>(
    () =>
        (isConfirmed.value && props.payoutMissing) ||
        (!isConfirmed.value && props.applicationPhase !== 'pending'),
);

/**
 * Полный aria-label для SR. Видимая надпись всегда «Наставник» — статус и
 * нюанс фазы озвучиваем здесь.
 */
const mentorAriaLabel = computed<string>(() => {
    if (isConfirmed.value) {
        return props.payoutMissing
            ? 'Статус: наставник, карта для выплат не привязана — нажмите, чтобы привязать'
            : 'Статус: наставник, профиль подтверждён';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'Статус: наставник, заявка не завершена';
        case 'pending':
            return 'Статус: наставник, заявка на подтверждении';
        case 'changes_requested':
            return 'Статус: наставник, требуются правки заявки';
        case 'rejected':
            return 'Статус: наставник, заявка отклонена';
        case 'revoked':
            return 'Статус: наставник, доступ отозван администратором';
        default:
            return 'Статус: наставник (не подтверждён)';
    }
});

// ── Тело tooltip ──────────────────────────────────────────────────────

const tooltipMessage = computed<string>(() => {
    if (isConfirmed.value) {
        return props.payoutMissing
            ? 'Карта для выплат не привязана — ваши платные услуги скрыты от учеников. Нажмите, чтобы привязать карту.'
            : 'Профиль наставника подтверждён.';
    }
    switch (props.applicationPhase) {
        case 'draft':
            return 'Вы не закончили оформление заявки наставника. Продолжите, чтобы отправить её на модерацию.';
        case 'pending':
            return 'Вы пока не являетесь полноценным наставником. Запись на ваши сессии откроется после того, как заявку подтвердят.';
        case 'changes_requested':
            return 'Модератор попросил правки. Откройте заявку и дополните недостающие поля.';
        case 'rejected':
            return props.rejectionReason
                ? `Заявка отклонена: ${props.rejectionReason}`
                : 'Заявка отклонена. Поправьте профиль и подайте её заново.';
        // Копия зеркалит платформенное уведомление mentor_app_revoked
        // («Доступ наставника приостановлен…»).
        case 'revoked':
            return 'Доступ наставника приостановлен. Причина и комментарий — в личном кабинете. Свяжитесь с поддержкой, если считаете это ошибкой.';
        default:
            return 'Вы пока не являетесь полноценным наставником.';
    }
});

// ── Взаимодействие ────────────────────────────────────────────────────

function onChipClick(): void {
    // Подтверждённый наставник без привязанной карты — приоритетнее всего,
    // отдаём отдельным каналом, чтобы контейнер навигировал в настройки
    // приёма денег без расширения Phase-енума.
    if (isConfirmed.value && props.payoutMissing) {
        // Мобильный (compact): на touch нет hover, поэтому первый тап
        // РАСКРЫВАЕТ tooltip («платные услуги никто не видит — привяжите
        // карту»), а уже повторный — ведёт в настройки выплат. На десктопе
        // tooltip доступен по hover, поэтому клик сразу навигирует (как было).
        if (props.compact && !tooltipOpen.value) {
            tooltipOpen.value = true;
            return;
        }
        tooltipOpen.value = false;
        emit('click-payout-attach');
        return;
    }
    // «На проверке» — действий нет, показываем модалку статуса прямо на месте
    // (работает в шапке/дашборде/меню без правок потребителей). Остальные
    // фазы по-прежнему отдаём наружу — потребитель навигирует (draft/rejected/
    // changes_requested → /become-mentor; approved → кабинет).
    if (props.applicationPhase === 'pending') {
        statusModalOpen.value = true;
        return;
    }
    emit('click-mentor-chip', props.applicationPhase);
}

/**
 * Vuetify v-tooltip закрывается по Escape, когда activator — focused
 * <button>. Дополнительно focus() на чип как защита, если Esc нажат пока
 * фокус был внутри тела tooltip.
 */
function onChipKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        tooltipOpen.value = false;
        mentorChipRef.value?.focus();
    }
}

return (_ctx: any,_cache: any) => {
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: "role-chips",
    role: "group",
    "aria-label": groupLabel,
    "data-testid": "user-role-chips"
  }, [
    (displayState.value === 'student')
      ? (_openBlock(), _createElementBlock("span", _hoisted_1, " Ученик "))
      : (_openBlock(), _createBlock(_component_v_tooltip, {
          key: 1,
          modelValue: tooltipOpen.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tooltipOpen).value = $event)),
          location: "bottom",
          "open-on-focus": !_ctx.compact,
          "open-on-hover": !_ctx.compact
        }, {
          activator: _withCtx(({ props: tooltipProps }) => [
            _createElementVNode("button", _mergeProps({
              ref_key: "mentorChipRef",
              ref: mentorChipRef
            }, tooltipProps, {
              type: "button",
              class: ["role-chip role-chip--mentor", [mentorChipClass.value, { 'role-chip--payout-alert': isPayoutAlert.value }]],
              "aria-label": mentorAriaLabel.value,
              "aria-describedby": tooltipId,
              "aria-current": isConfirmed.value ? 'true' : undefined,
              "aria-busy": isPending.value ? 'true' : undefined,
              "aria-expanded": _ctx.compact ? String(tooltipOpen.value) : undefined,
              "data-testid": "role-chip-mentor",
              onClick: onChipClick,
              onKeydown: onChipKeydown
            }), [
              (isPending.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3))
                : _createCommentVNode("", true),
              (isPayoutAlert.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4))
                : _createCommentVNode("", true),
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "role-chip__label" }, "Наставник", -1)),
              (mentorSuffix.value)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 2,
                    class: _normalizeClass(["role-chip__suffix", { 'role-chip__suffix--action': suffixNeedsAction.value }]),
                    "data-testid": "role-chip-suffix"
                  }, "· " + _toDisplayString(mentorSuffix.value), 3))
                : _createCommentVNode("", true)
            ], 16, _hoisted_2)
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", {
              id: tooltipId,
              class: "role-chip-tooltip",
              "data-testid": "role-chip-tooltip"
            }, [
              _createElementVNode("p", _hoisted_5, _toDisplayString(tooltipMessage.value), 1)
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "open-on-focus", "open-on-hover"])),
    _createVNode(MentorApplicationStatusModal, {
      modelValue: statusModalOpen.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((statusModalOpen).value = $event)),
      phase: _ctx.applicationPhase,
      "rejection-reason": _ctx.rejectionReason
    }, null, 8, ["modelValue", "phase", "rejection-reason"])
  ]))
}
}

})